import type { FC, ReactElement, ReactNode } from "react";
import { useCallback, useState } from "react";
import { Button as AriaButton } from "react-aria-components";
import { DisplayInput, DisplayTextarea } from "@/components/Atoms/DisplayField";
import {
	DisplayFieldFeedback,
	DisplayFieldState,
} from "@/components/Atoms/DisplayField/display-field.shared";
import { DisplayFieldShell } from "@/components/Atoms/DisplayField/DisplayFieldShell";
import { useFieldFeedbackMap } from "@/components/Atoms/FeedbackIndicator";
import { IconPickerPopover } from "@/components/Organisms/IconPickerPopover/IconPickerPopover";
import { formatId } from "@/utils/format-id";

/** Partial update persisted by the host when a Details row is committed. */
export interface EntityTypeDetailsPatch {
	name?: string;
	description?: string | null;
	icon?: string | null;
}

export interface EntityTypeDetailsLabels {
	name: string;
	icon: string;
	source: string;
	lifecycle: string;
	id: string;
	description: string;
	nameRequired: string;
	saveFailed: string;
}

export interface EntityTypeDetailsPanelProps {
	labels: EntityTypeDetailsLabels;
	name: string | null | undefined;
	icon: string | null | undefined;
	description: string | null | undefined;
	sourceLabel: string;
	/** Lifecycle row value; row renders empty when nullish. */
	lifecycleLabel?: string | null;
	/** Record types have no lifecycle row. */
	showLifecycle: boolean;
	id: string;
	/** Resolves the icon badge glyph for the current icon value. */
	resolveIcon: (icon: string | null | undefined) => FC<{ className?: string }>;
	/** False for asset types (icon is auto-selected). */
	iconEditable: boolean;
	/** Extra read-only rows rendered after Lifecycle (e.g. asset Geometry). */
	extraRows?: ReactNode;
	/** Required Location Yes/No, rendered after ID and before attachments. */
	locationRequiredField?: ReactNode;
	/** Required attachments field, rendered after ID and before Description. */
	attachmentsField: ReactNode;
	canEdit: boolean;
	onPersist: (patch: EntityTypeDetailsPatch) => Promise<void>;
	"data-testid"?: string;
}

const EditableField = {
	Name: "name",
	Icon: "icon",
	Description: "description",
} as const;

type EditableField = (typeof EditableField)[keyof typeof EditableField];

/**
 * Entity-type drawer Details tab ΓÇö Figma DisplayField stack with per-row
 * inline edit (save on blur/Enter, Escape discards).
 *
 * @see Figma Entity-type tables / Drawer - Details 28:59025
 */
export function EntityTypeDetailsPanel({
	labels,
	name,
	icon,
	description,
	sourceLabel,
	lifecycleLabel,
	showLifecycle,
	id,
	resolveIcon,
	iconEditable,
	extraRows,
	locationRequiredField,
	attachmentsField,
	canEdit,
	onPersist,
	"data-testid": dataTestId = "entity-type-details-panel",
}: EntityTypeDetailsPanelProps): ReactElement {
	const [editingField, setEditingField] = useState<EditableField | null>(null);
	const [draftByField, setDraftByField] = useState<
		Partial<Record<EditableField, string>>
	>({});
	const [errorByField, setErrorByField] = useState<
		Partial<Record<EditableField, string>>
	>({});
	const { feedbackByField, setFieldFeedback } =
		useFieldFeedbackMap<EditableField>();

	const serverValue = useCallback(
		(field: EditableField): string => {
			if (field === EditableField.Name) return name ?? "";
			if (field === EditableField.Description) return description ?? "";
			return icon ?? "";
		},
		[name, description, icon],
	);

	const setError = (field: EditableField, message: string | undefined) => {
		setErrorByField((prev) => ({ ...prev, [field]: message }));
	};

	const clearDraft = (field: EditableField) => {
		setDraftByField((prev) => {
			const next = { ...prev };
			delete next[field];
			return next;
		});
	};

	const persist = async (
		field: EditableField,
		patch: EntityTypeDetailsPatch,
	): Promise<boolean> => {
		setFieldFeedback(field, DisplayFieldFeedback.Saving);
		try {
			await onPersist(patch);
			setError(field, undefined);
			setFieldFeedback(field, DisplayFieldFeedback.Saved);
			return true;
		} catch {
			setFieldFeedback(field, DisplayFieldFeedback.Idle);
			setError(field, labels.saveFailed);
			return false;
		}
	};

	const beginEdit = (field: EditableField) => {
		setError(field, undefined);
		setEditingField(field);
		setDraftByField((prev) => ({ ...prev, [field]: serverValue(field) }));
	};

	const cancelEdit = (field: EditableField) => {
		setError(field, undefined);
		setFieldFeedback(field, DisplayFieldFeedback.Idle);
		clearDraft(field);
		setEditingField((current) => (current === field ? null : current));
	};

	const handleDraftChange = (field: EditableField, value: string) => {
		setDraftByField((prev) => ({ ...prev, [field]: value }));
		setError(field, undefined);
	};

	const commitText = async (field: EditableField) => {
		const raw = draftByField[field];
		if (raw === undefined) {
			setEditingField((current) => (current === field ? null : current));
			return;
		}
		const next = field === EditableField.Name ? raw.trim() : raw;
		if (next === serverValue(field)) {
			cancelEdit(field);
			return;
		}
		if (field === EditableField.Name && !next) {
			setError(field, labels.nameRequired);
			return;
		}
		setEditingField((current) => (current === field ? null : current));
		const ok = await persist(
			field,
			field === EditableField.Name
				? { name: next }
				: { description: next || null },
		);
		if (ok) {
			clearDraft(field);
		}
	};

	const commitIcon = async (nextIcon: string) => {
		if (nextIcon === (icon ?? "")) return;
		await persist(EditableField.Icon, { icon: nextIcon });
	};

	const fieldState = (field: EditableField): DisplayFieldState => {
		if (errorByField[field]) return DisplayFieldState.Error;
		if (editingField === field) return DisplayFieldState.Active;
		return DisplayFieldState.Enabled;
	};

	const isEditing = (field: EditableField) => editingField === field;
	const nameDraft = draftByField[EditableField.Name] ?? name ?? "";
	const descriptionDraft =
		draftByField[EditableField.Description] ?? description ?? "";

	const IconGlyph = resolveIcon(icon);
	const iconBadge = (
		<span className="flex size-8 shrink-0 items-center justify-center rounded-full bg-secondary ring-1 ring-secondary">
			<IconGlyph className="size-4 text-fg-secondary" aria-hidden />
		</span>
	);
	const iconShellProps = {
		label: labels.icon,
		state: fieldState(EditableField.Icon),
		isPopulated: true,
		feedback: feedbackByField[EditableField.Icon] ?? DisplayFieldFeedback.Idle,
		hintText: errorByField[EditableField.Icon],
		showHintText: Boolean(errorByField[EditableField.Icon]),
		"data-testid": "entity-type-icon-field",
	};

	return (
		<div className="flex w-full flex-col" data-testid={dataTestId}>
			<DisplayInput
				label={labels.name}
				value={isEditing(EditableField.Name) ? nameDraft : name || undefined}
				state={fieldState(EditableField.Name)}
				feedback={
					feedbackByField[EditableField.Name] ?? DisplayFieldFeedback.Idle
				}
				hintText={errorByField[EditableField.Name]}
				showHintText={Boolean(errorByField[EditableField.Name])}
				showEdit={canEdit}
				editValue={isEditing(EditableField.Name) ? nameDraft : undefined}
				onEdit={canEdit ? () => beginEdit(EditableField.Name) : undefined}
				onEditChange={(value) => handleDraftChange(EditableField.Name, value)}
				onEditCommit={() => void commitText(EditableField.Name)}
				onEditCancel={() => cancelEdit(EditableField.Name)}
				data-testid="entity-type-name-field"
			/>
			{canEdit && iconEditable ? (
				<IconPickerPopover
					value={icon ?? ""}
					onChange={(next) => void commitIcon(next)}
					trigger={
						<AriaButton
							className="w-full border-0 bg-transparent p-0 text-left outline-focus-ring"
							data-testid="entity-type-icon-trigger"
						>
							<DisplayFieldShell
								{...iconShellProps}
								showEdit={iconShellProps.feedback === DisplayFieldFeedback.Idle}
								isTriggerChild
							>
								{iconBadge}
							</DisplayFieldShell>
						</AriaButton>
					}
				/>
			) : (
				<DisplayFieldShell {...iconShellProps} showEdit={false}>
					{iconBadge}
				</DisplayFieldShell>
			)}
			<DisplayInput
				label={labels.source}
				value={sourceLabel}
				state={DisplayFieldState.Disabled}
				showEdit={false}
				data-testid="entity-type-source-field"
			/>
			{showLifecycle ? (
				<DisplayInput
					label={labels.lifecycle}
					value={lifecycleLabel || undefined}
					state={DisplayFieldState.Enabled}
					showEdit={false}
					data-testid="entity-type-lifecycle-field"
				/>
			) : null}
			{extraRows}
			<DisplayInput
				label={labels.id}
				value={formatId(id)}
				state={DisplayFieldState.Enabled}
				copyable
				copyText={id}
				showEdit={false}
				data-testid="entity-type-id-field"
			/>
			{locationRequiredField}
			{attachmentsField}
			<DisplayTextarea
				label={labels.description}
				value={
					isEditing(EditableField.Description)
						? descriptionDraft
						: description || undefined
				}
				state={fieldState(EditableField.Description)}
				feedback={
					feedbackByField[EditableField.Description] ??
					DisplayFieldFeedback.Idle
				}
				hintText={errorByField[EditableField.Description]}
				showHintText={Boolean(errorByField[EditableField.Description])}
				showEdit={canEdit}
				editValue={
					isEditing(EditableField.Description) ? descriptionDraft : undefined
				}
				onEdit={
					canEdit ? () => beginEdit(EditableField.Description) : undefined
				}
				onEditChange={(value) =>
					handleDraftChange(EditableField.Description, value)
				}
				onEditCommit={() => void commitText(EditableField.Description)}
				onEditCancel={() => cancelEdit(EditableField.Description)}
				data-testid="entity-type-description-field"
			/>
		</div>
	);
}
